import {isFilled} from '@prismicio/client'
import {PrismicNextImage} from '@prismicio/next/pages'
import type {EventDocument} from 'prismicio-types'
import {memo, useEffect, useMemo, useState} from 'react'
import {Container} from 'src/common/Container'
import {useGetEventStatus} from 'src/common/hooks/useGetEventStatus'
import {formatEventTypeDisplay} from 'src/common/utils/eventRoutes'
import {ActionButton} from './components/ActionButton'
import {InfoRow} from './components/InfoRow'
import {Tag} from './components/Tag'
import {VideoEmailDialog} from './components/VideoEmailDialog'
import {VideoPreview} from './components/VideoPreview'
import {convertContrastVideoWithoutAutoplay, formatDate, getDuration, getVideoUnlockedCookie} from './helpers'

type Props = {
  pageUid: string
  data: EventDocument['data']
}

export const Hero = memo(({data, pageUid}: Props) => {
  const eventStatus = useGetEventStatus({
    eventDate: data.event_date,
    eventFinishDate: data.event_finish_date,
    eventType: isFilled.select(data.event_type) ? data.event_type : null,
  })

  const duration = useMemo(() => getDuration(data), [data])
  const [videoUnlocked, setVideoUnlocked] = useState(false)
  const [isCheckingCookie, setIsCheckingCookie] = useState(true)
  const [isIframeLoading, setIsIframeLoading] = useState(false)
  const [unlockedFromCookie, setUnlockedFromCookie] = useState(false)

  const onDemand = data.event_type === 'On demand'

  // Check cookie to restore unlocked state
  useEffect(() => {
    if (!isFilled.keyText(data.contrast_video_url)) {
      setIsCheckingCookie(false)
      return
    }

    const isUnlocked = getVideoUnlockedCookie(data.contrast_video_url)

    if (isUnlocked) {
      setVideoUnlocked(true)
      setIsIframeLoading(true)
      setUnlockedFromCookie(true)
    }

    setIsCheckingCookie(false)
  }, [data.contrast_video_url])

  const handleEmailSubmitSuccess = () => {
    setVideoUnlocked(true)
    setIsIframeLoading(true)
    setUnlockedFromCookie(false)
  }

  const handleIframeLoad = () => {
    setIsIframeLoading(false)
  }

  return (
    <Container className="relative mx-auto scroll-mt-16 px-5 !pb-0 md:px-10 lg:px-5 xl:px-0" backgroundColor="Grey/100">
      <div className="w-full min-w-0 max-w-[1200px] pt-18 md:pt-16 lg:pt-20">
        <div className="flex flex-col gap-6 rounded-lg border border-black/10 bg-moss-white p-6 md:p-8 lg:flex-row lg:gap-8">
          <div className="flex flex-col gap-4 md:gap-0 lg:w-[400px]">
            <div className="mb-0 flex gap-2 md:mb-4">
              {!onDemand && eventStatus && <Tag variant={eventStatus.variant}>{eventStatus.label}</Tag>}
              {isFilled.select(data.event_type) && (onDemand || eventStatus?.label !== 'PAST EVENT') && (
                <Tag variant="secondary">{formatEventTypeDisplay(data.event_type)}</Tag>
              )}
            </div>

            {isFilled.keyText(data.title) && (
              <h1 className="mb-0 text-32 font-medium leading-[38px] text-moss-black md:mb-4 lg:mb-6">{data.title}</h1>
            )}

            {isFilled.keyText(data.event_description) && (
              <p className="mb-0 text-15 font-medium leading-5 text-moss-grey-400 md:mb-4 lg:mb-8">
                {data.event_description}
              </p>
            )}

            <div className="mb-0 flex flex-col gap-3 py-3 pb-0 md:flex-row md:gap-6 lg:mb-8 lg:flex-col lg:gap-0 lg:py-0 lg:[&>*:first-child]:pt-0 lg:[&>*:not(:last-child)]:border-b lg:[&>*:not(:last-child)]:border-grey-100 lg:[&>*:not(:last-child)]:pb-3 lg:[&>*]:pt-3">
              {isFilled.timestamp(data.event_date) && !onDemand && (
                <InfoRow text={formatDate(data.event_date)} iconName="calendar-filled" />
              )}

              {!data.hide_time_and_duration && duration && <InfoRow text={duration} iconName="clock-filled" />}
              {isFilled.keyText(data.language) && <InfoRow text={data.language} iconName="requests-filled" />}
              {isFilled.keyText(data.location) && !onDemand && (
                <InfoRow text={data.location} iconName="map-pin-filled" />
              )}
            </div>

            <ActionButton data={data} className="mt-auto hidden w-fit px-4 py-3 lg:block" />
          </div>

          {onDemand && isFilled.keyText(data.contrast_video_url) && (
            <>
              {(isCheckingCookie || isIframeLoading) && (
                <div
                  className="relative flex w-full items-center justify-center overflow-hidden rounded-[4px] bg-moss-grey-100 lg:flex-1 lg:self-start"
                  style={{aspectRatio: '16/9'}}
                >
                  <div className="size-12 animate-spin rounded-full border-4 border-moss-grey-300 border-t-moss-black" />
                </div>
              )}
              {!isCheckingCookie && !videoUnlocked && isFilled.image(data.hero_image) && <VideoPreview data={data} />}
              {!isCheckingCookie && videoUnlocked && (
                <div
                  className="relative w-full overflow-hidden rounded-[4px] lg:flex-1 lg:self-start"
                  style={{aspectRatio: '16/9', display: isIframeLoading ? 'none' : 'block'}}
                >
                  <iframe
                    src={
                      unlockedFromCookie
                        ? convertContrastVideoWithoutAutoplay(data.contrast_video_url)
                        : data.contrast_video_url
                    }
                    className="size-full"
                    title="Contrast video player"
                    allow="encrypted-media; picture-in-picture; web-share"
                    onLoad={handleIframeLoad}
                    allowFullScreen
                  />
                </div>
              )}
              <VideoEmailDialog
                onSubmitSuccess={handleEmailSubmitSuccess}
                data={data}
                pageUid={pageUid}
                videoUrl={data.contrast_video_url}
              />
            </>
          )}

          {!onDemand && isFilled.image(data.hero_image) && (
            <div
              className="relative w-full overflow-hidden rounded-[4px] lg:flex-1 lg:self-start"
              style={{aspectRatio: '16/9'}}
            >
              <PrismicNextImage
                field={data.hero_image}
                className="size-full object-cover"
                priority
                sizes="(max-width: 768px) 100vw, (max-width: 1024px) 100vw, 800px"
              />
            </div>
          )}

          <ActionButton data={data} className="w-fit px-4 py-3 lg:hidden" />
        </div>
      </div>
    </Container>
  )
})
